import axios from 'helpers/axios';
import { useCancelToken, useMutableRef, useToasts } from 'hooks';
import React from 'react';
import { DataTable, DataTableCell } from 'ui/components/shared';
import { Button, Container } from 'ui/components/styled';

export const CustomersList = ({
  company,
  member,
  setSelectedCustomers,
  selectedCustomers,
  refresh,
  closeModal,
}) => {
  const [data, setData] = React.useState([]);
  const [allData, setAllData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [totalRows, setTotalRows] = React.useState(0);
  const [currentPage, setCurrentPage] = useMutableRef(1);
  const [perPage] = React.useState(15);
  const [search, setSearch] = React.useState('');
  const { errorToast, successToast } = useToasts();
  const { cancelPreviousRequest, isCancel } = useCancelToken();
  const columns = [
    {
      name: 'Customer',
      selector: 'name',
      // eslint-disable-next-line react/display-name
      cell: (row) => <DataTableCell title={row.name}>{row.name}</DataTableCell>,
      sortable: true,
    },
  ];

  const handleSubmit = async () => {
    setLoading(true);

    try {
      const response = await axios.post(
        `/companies/${company?.id}/members/${member?.id}/customers?$search=${search}`,
        selectedCustomers
      );

      if (response.status === 200) {
        successToast("Member's list customers updated successfully");
        refresh();
        closeModal();
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Fetch Member customers.' });
    }

    setLoading(false);
  };

  const initSelectedCustomers = () => {
    setSelectedCustomers(() => {
      if (member?.filterCustomers?.includes('all')) {
        return {
          filterCustomers: 'all',
          customers: [],
        };
      }

      if (member?.filterCustomers?.includes('none')) {
        return {
          filterCustomers: 'none',
          customers: [],
        };
      }

      if (member?.filterCustomers?.includes('custom')) {
        return {
          filterCustomers: 'custom',
          customers: member?.customers,
        };
      }
    });
  };

  const setSelected = (customer) => {
    if (member?.filterCustomers?.includes('all')) return true;

    if (member?.filterCustomers?.includes('none')) return false;

    if (member?.filterCustomers?.includes('custom'))
      member.customers.findIndex((item) => customer._id === item._id) > -1;
  };

  const fetchMemberCutomers = async () => {
    setLoading(true);

    try {
      const response = await axios.get(
        `/companies/${company?.id}/members/${member?.id}/customers?$search=${search}`
      );

      if (response.status === 200) {
        setData(response.data.value);
        setAllData(response.data.value.map((item) => ({ ...item, selected: setSelected(item) })));
        setTotalRows(response.data.count);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Fetch Member customers.' });
    }

    setLoading(false);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFilter = async (value) => {
    setSearch(value);

    cancelPreviousRequest();

    try {
      if (value.length > 1) {
        const filteredData = data.filter((customer) =>
          customer.name.toLowerCase().includes(value.trim().toLowerCase())
        );
        setData(filteredData);
        setTotalRows(filteredData.length);
      }
    } catch (error) {
      if (isCancel(error)) return;
      errorToast({ error, default: "Error: Filter member's customers." });
    }
  };

  const handleSelectedRowsChange = async ({ selectedRows }) => {
    // select the rows from all our data that are really selected
    const newData = allData.map((item) => {
      if (data.findIndex((customer) => customer._id === item._id) > -1) {
        item.selected = selectedRows.findIndex((row) => row._id === item._id) > -1;
      }
      return item;
    });

    // get the selected ones
    const currentlySelectedCustomers = newData
      .filter((customer) => customer.selected)
      .map((item) => item._id);

    // get the state of the filter customers
    const selectedCustomersFilter =
      currentlySelectedCustomers.length === 0
        ? 'none'
        : currentlySelectedCustomers.length === allData.length
        ? 'all'
        : 'custom';

    setSelectedCustomers({
      filterCustomers: selectedCustomersFilter,
      customers: ['all', 'none'].includes(selectedCustomersFilter)
        ? []
        : currentlySelectedCustomers,
    });
  };

  const handleIsRowSelected = (row) => {
    if (selectedCustomers?.filterCustomers === 'all') return true;
    if (selectedCustomers?.filterCustomers === 'none') return false;
    return (
      selectedCustomers?.filterCustomers === 'custom' &&
      selectedCustomers?.customers.includes(row._id)
    );
  };

  React.useEffect(() => {
    if (member) {
      fetchMemberCutomers();
      initSelectedCustomers();
    }
  }, [member]);

  React.useEffect(() => {
    if (search.length === 0 && member) {
      fetchMemberCutomers();
    }
  }, [search, member]);

  return (
    <>
      <DataTable
        columns={columns}
        data={data}
        progressPending={loading}
        paginationPerPage={perPage}
        paginationRowsPerPageOptions={[5, 10, 15, 20]}
        paginationComponentOptions={{
          selectAllRowsItem: false,
        }}
        paginationTotalRows={totalRows}
        paginationCurrentPage={currentPage.current}
        onPageChange={handlePageChange}
        onPageReset={setCurrentPage}
        filterPlaceholder="Customers Search"
        onFilter={handleFilter}
        subHeaderTitle={`${member?.user?.name}'s Customers list`}
        noPadding
        paginationServer={false}
        sortServer={false}
        selectableRows
        onSelectedRowsChange={handleSelectedRowsChange}
        selectableRowSelected={handleIsRowSelected}
      />
      <Container flexDirection="row" gap="24px" mt="24px">
        <Button variant="secondary" onClick={closeModal}>
          Cancel
        </Button>
        <Button onClick={handleSubmit}> Save</Button>
      </Container>
    </>
  );
};
